import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TemperMark } from "../components/Logo";
import { MaskedLines, FadeUp, ParallaxImage } from "../components/Reveal";
import Seo from "../components/Seo";

const PORTRAIT = "/eric-portrait.jpg";

const METHOD = [
  {
    n: "01",
    t: "Heat",
    d: "An honest read. Assessment, hard questions, and the picture you may have been avoiding.",
  },
  {
    n: "02",
    t: "Hold",
    d: "Deliberate practice. Real scenarios, direct feedback, and accountability between sessions.",
  },
  {
    n: "03",
    t: "Temper",
    d: "Strength balanced with warmth — tested under pressure until it holds.",
  },
];

const BELIEFS = [
  ["Candor", "over comfort."],
  ["Warmth", "over softness."],
  ["Strength", "that serves."],
];

export default function About() {
  return (
    <>
      <Seo
        title="About Eric Ligeikis — Leadership Coach & Founder | TEMPER"
        description="Eric Ligeikis brings 25+ years in public service, hospitality, and organizational leadership to his coaching. Discipline plus warmth, tested under pressure."
        path="/about"
      />
      <section data-testid="about-header" className="pt-32 md:pt-44">
        <div className="mx-auto grid max-w-[1440px] gap-14 px-6 md:grid-cols-12 md:px-10">
          <div className="md:col-span-7">
            <FadeUp>
              <p className="eyebrow flex items-center gap-3 text-ink/60">
                <TemperMark className="h-3 w-3 text-ember" /> About
              </p>
            </FadeUp>
            <MaskedLines
              as="h1"
              className="mt-8 font-display text-[clamp(3rem,7.5vw,7rem)] font-black leading-[0.95] tracking-[-0.03em]"
              lines={["Eric", <span key="x"><span className="text-ember">Ligeikis.</span></span>]}
            />
            <FadeUp className="mt-8" delay={0.15}>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-steel">
                Founder & Head Coach — Temper Leadership
              </p>
            </FadeUp>
          </div>
          <div className="md:col-span-5">
            <FadeUp delay={0.2}>
              <ParallaxImage
                src={PORTRAIT}
                alt="Portrait of Eric Ligeikis, founder of TEMPER Leadership"
                className="aspect-[3/4] w-full"
                caption="Eric Ligeikis — Founder, Temper Leadership"
                loading="eager"
              />
            </FadeUp>
          </div>
        </div>
      </section>

      <section data-testid="about-bio" className="py-24 md:py-36">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-6 md:grid-cols-12 md:px-10">
          <div className="md:col-span-3">
            <FadeUp>
              <p className="eyebrow text-ember">The story</p>
            </FadeUp>
          </div>
          <div className="max-w-3xl space-y-8 text-lg leading-relaxed text-steel md:col-span-9">
            <FadeUp>
              <p>
                Eric Ligeikis brings more than 25 years of leadership experience across
                public service, hospitality, and organizational leadership — rooms where the
                stakes are real and the pressure doesn't wait for you to be ready.
              </p>
            </FadeUp>
            <FadeUp>
              <p>
                He founded TEMPER Leadership on a blacksmith's observation: raw strength, on
                its own, shatters. What survives the heat is strength that has been balanced
                — worked, cooled, and tested.
              </p>
            </FadeUp>
            <FadeUp>
              <p>
                His coaching pairs the two things leaders are usually told to choose between.
                Discipline: honest assessment, direct feedback, standards that don't bend.
                Warmth: real attention to the humans doing the work. Held together, they make
                leaders people choose to follow — not people they merely report to.
              </p>
            </FadeUp>
            <FadeUp>
              <p>
                That combination was forged across decades of serving the public, running
                demanding operations, and building leaders inside complex organizations. Eric
                works one-on-one with leaders, builds teams together in the room, and advises
                organizations that want leadership to hold at every level — not just at the
                top.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <section data-testid="about-method" className="border-t border-line py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <FadeUp>
            <p className="eyebrow text-ember">The tempering method</p>
          </FadeUp>
          <div className="mt-14 grid gap-px border border-line bg-line md:grid-cols-3">
            {METHOD.map((m, i) => (
              <FadeUp key={m.n} delay={i * 0.08} className="bg-paper p-8 md:p-10">
                <span className="font-mono text-[11px] tracking-[0.2em] text-ember">{m.n}</span>
                <h3 className="mt-5 font-display text-3xl font-extrabold tracking-tight">{m.t}</h3>
                <p className="mt-4 leading-relaxed text-steel">{m.d}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-beliefs" className="py-24 md:py-36">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <div className="space-y-6">
            {BELIEFS.map((b, i) => (
              <MaskedLines
                key={b[0]}
                delay={i * 0.05}
                className="font-display text-4xl font-extrabold tracking-tight md:text-6xl"
                lines={[
                  <span key="y">
                    {b[0]} <span className="text-ember">{b[1]}</span>
                  </span>,
                ]}
              />
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-cta" className="border-t border-line py-24 md:py-32">
        <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-10 px-6 md:flex-row md:items-end md:px-10">
          <MaskedLines
            className="font-display text-4xl font-extrabold leading-[1.02] tracking-tight md:text-6xl"
            lines={["Bring your team", <span key="x">in from the <span className="text-ember">heat.</span></span>]}
          />
          <FadeUp className="flex flex-wrap gap-4">
            <Link to="/contact" data-testid="about-contact-cta" className="btn-ink">
              Start the conversation <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/temper-profile" className="btn-outline">
              Take the TEMPER Profile
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
